import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import InSilicoPredictionsScoresRow from "./InSilicoPredictionsScoresRow";
import { getUserIsPremium } from "../../../../../../state/selectors/CardsUI.selectors";
import HelperText from "../../../../../../components/atoms/HelperText";
import { ToggleButton } from "../../../../../../components/atoms";
import { approxHoneyDew } from "../../../../../../common/helpers/colors";

const sortingPathoArray = [
  "Pathogenic Very Strong",
  "Bening Very Strong",
  "Pathogenic Strong",
  "Benign Strong",
  "Pathogenic Moderate",
  "Benign Moderate",
  "Pathogenic Supporting",
  "Benign Supporting",
  "Uncertain",
];

const InSilicoPredictionsScoresTable = ({
  insilicoDbs,
  isUserPremium,
  varsomeHost,
}) => {
  if (!insilicoDbs || !insilicoDbs.length) return <div>No data available</div>;
  const [showRawData, setShowRawData] = useState(false);
  const [sortAlphabetically, setSortAlphabetically] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [initialData, setInitialData] = useState(insilicoDbs);
  const [data, setData] = useState(insilicoDbs);

  const tableWidth = showRawData ? "100%" : "60%";
  const versionWidth = showRawData ? "10%" : "15%";
  const scoresWidth = showRawData ? "20%" : "35%";

  useEffect(() => {
    if (!sortAlphabetically) {
      const dataSortedByPathogenicity = [...data].sort((a, b) => {
        return (
          sortingPathoArray.indexOf(a.calibratedPrediction) -
          sortingPathoArray.indexOf(b.calibratedPrediction)
        );
      });
      setData(dataSortedByPathogenicity);
    } else {
      setData(initialData);
    }
  }, [sortAlphabetically]);

  return (
    <>
      <div className="tw-flex tw-mb-2">
        <div className="tw-flex">
          <ToggleButton
            onToggleClick={(e) => {
              setShowRawData(e.target.checked);
            }}
            label="Show raw data"
            isChecked={showRawData}
            noPaddingAndMargin={true}
          />
        </div>
        <div className="tw-flex">
          <ToggleButton
            onToggleClick={(e) => {
              setSortAlphabetically(e.target.checked);
            }}
            label="Alphabetically"
            isChecked={sortAlphabetically}
            noPaddingAndMargin={true}
          />
        </div>
      </div>
      <div>
        <table
          className="varsome-table varsome-table-minimum-padding"
          data-testid="insilico_scores_based_table"
          style={{ backgroundColor: approxHoneyDew, width: tableWidth }}
        >
          <thead>
            <tr>
              <th>Engine</th>
              <th>
                Calibrated Prediction
                <HelperText
                  title=""
                  RenderContent={() => (
                    <>
                      Prediction based on the in-silico calibrations performed
                      by Saphetor - see
                      <a
                        href={`${varsomeHost}/about/germline-implementation/#insilicopredictions`}
                        target="_blank"
                      >
                        {" Germline Variant Classification InSilico Calbration"}
                      </a>
                      .
                    </>
                  )}
                />
              </th>
              <th width={scoresWidth}>
                Score
                <HelperText
                  title=""
                  RenderContent={() => (
                    <>
                      Retrieved scores from the different engines. Each score
                      corresponds to a specific transcript and is colored based
                      on its Calibrated Prediction - see
                      <a
                        href={`${varsomeHost}/about/germline-implementation/#insilicopredictions`}
                        target="_blank"
                      >
                        {
                          " Germline Variant Classification InSilico Calibration"
                        }
                      </a>
                      . Hover over the score to see the transcript reference and
                      the calibrated prediction.
                    </>
                  )}
                />
              </th>
              {showRawData && (
                <>
                  <th width="20%">
                    Indicative Prediction
                    <HelperText
                      title=""
                      customText="Original uncalibrated prediction provided by the source database."
                    />
                  </th>
                  <th width="9%">Rankscore</th>
                  <th width="9%" />
                </>
              )}
              <th width={versionWidth}>Version</th>
            </tr>
          </thead>
          <tbody>
            {data.map((db, idx) => (
              <InSilicoPredictionsScoresRow
                showRawData={showRawData}
                isUserPremium={isUserPremium}
                database={db}
                key={`${db.name}-row-${db.url}-patho-score-db-${idx}`}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

InSilicoPredictionsScoresTable.propTypes = {
  insilicoDbs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isUserPremium: PropTypes.bool,
  varsomeHost: PropTypes.string,
};

InSilicoPredictionsScoresTable.defaultProps = {
  isUserPremium: false,
  varsomeHost: null,
};

const mapStateToProps = (state) => ({
  isUserPremium: getUserIsPremium(state),
  varsomeHost: state.varsomeHost,
});

export default connect(mapStateToProps)(
  React.memo(InSilicoPredictionsScoresTable)
);
